/**
 * SEO Head Component
 * Updates document head with page-specific meta tags and tracks page views
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteByPath, ROUTES } from '../config/routes';
import { analytics } from '../services/analyticsService';
import { ENV } from '../config/env';

const BASE_URL = ENV.SITE_URL;

// Structured data for different page types
const getStructuredData = (route: { path: string; title: string; description: string }) => {
  const baseData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${BASE_URL}${route.path}`,
    name: route.title,
    description: route.description,
    url: `${BASE_URL}${route.path}`,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${BASE_URL}/#website`,
      name: 'ImageLab',
      url: BASE_URL,
    },
  };

  // Add tool-specific structured data
  if (route.path === '/' || route.path.includes('converter') || route.path.includes('generator') || route.path.includes('compressor') || route.path.includes('editor')) {
    return {
      ...baseData,
      '@type': 'SoftwareApplication',
      applicationCategory: 'WebApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    };
  }

  return baseData;
};

export function SeoHead() {
  const location = useLocation();
  
  useEffect(() => {
    const route = getRouteByPath(location.pathname) || ROUTES[0];
    
    // Track page view in Google Analytics
    analytics.pageView(location.pathname, route.title);
    
    // Update title
    document.title = route.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', route.description);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', route.keywords);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', route.title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', route.description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${BASE_URL}${route.path}`);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', route.title);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', route.description);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${BASE_URL}${route.path}`);
    }

    // Update or add dynamic structured data
    const structuredDataId = 'dynamic-structured-data';
    let existingScript = document.getElementById(structuredDataId);
    
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.id = structuredDataId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(getStructuredData(route));
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById(structuredDataId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
    
  }, [location.pathname]);
  
  return null;
}
